import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Video, Play, TrendingUp, Loader2, Sparkles, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Phase 26 · Horizontal trending videos carousel for MarketplaceHub.
 * Injected surgically — auto-fetches /api/videos/trending?days=7.
 * Hidden when zero results.
 */
export function VideoTrendingCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/videos/trending?days=7&limit=8`, {
          credentials: "include",
        });
        if (r.ok) {
          const d = await r.json();
          setItems(d.items || []);
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <section data-testid="mp-video-carousel-loading" className="text-xs text-text-secondary py-4">
        <Loader2 className="h-3 w-3 animate-spin inline mr-2" /> Loading videos…
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section data-testid="mp-video-trending-carousel" className="space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
            <Video className="h-3 w-3 text-[var(--color-secondary)]" /> AI Videos · Trending 7d
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-[var(--color-text-main)]">
            Short-form video drops
          </h2>
        </div>
        <Link
          to="/videos"
          data-testid="mp-video-carousel-see-all"
          className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] hover:opacity-80"
        >
          See all →
        </Link>
      </header>

      <div className="grid grid-flow-col auto-cols-[minmax(220px,1fr)] gap-3 overflow-x-auto pb-3 snap-x snap-mandatory">
        {items.map((v) => {
          const grad = v.thumbnail_gradient || {};
          const bg = grad.start_color
            ? `linear-gradient(${grad.angle || 135}deg, ${grad.start_color}, ${grad.end_color || grad.start_color})`
            : "linear-gradient(135deg, #FFFFFF, #CCCCCC)";
          return (
            <Link
              key={v.video_id}
              to={`/video/${encodeURIComponent(v.video_id)}`}
              data-testid={`mp-video-card-${v.video_id}`}
              className="group snap-start rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden hover:border-[var(--color-secondary)]/60 transition-colors block"
            >
              <div className="relative aspect-video" style={{ background: bg }}>
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neutral-400/30 text-neutral-100 border border-neutral-400/40 backdrop-blur">
                  <TrendingUp className="h-2.5 w-2.5" /> {v.view_count || 0}v
                </div>
                <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-black/40 text-white/90 border border-white/20 backdrop-blur">
                  {v.duration_sec}s
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="h-4 w-4 text-white ml-0.5" fill="currentColor" />
                  </div>
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#CCCCCC]-300">
                  <Sparkles className="h-2.5 w-2.5" /> AI Video
                </div>
                <h3 className="text-xs font-semibold text-[var(--color-text-main)] line-clamp-2 group-hover:text-[var(--color-secondary)]">
                  {v.title}
                </h3>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default VideoTrendingCarousel;
